import { ProductVariant, VariantInventoryState } from '@features/catalog/models/product.models';

export type InventoryClaimRow = {
  inventoryManaged?: boolean | null;
  inventoryItemId?: string | null;
  stockQuantity?: number | null;
};

type InventoryManagedRow = Pick<ProductVariant, 'inventoryManaged'>;

function asManagedRows(
  variants: ReadonlyArray<InventoryClaimRow> | null | undefined
): ReadonlyArray<InventoryManagedRow> {
  return (variants ?? []).map((variant) => ({
    inventoryManaged: variant.inventoryManaged ?? undefined,
  }));
}

/**
 * Estado agregado de inventario para productos variables. Prioriza el valor
 * calculado por el backend (`Product#variable_inventory_state`); si no viene
 * (p. ej. drafts locales o respuestas parciales), lo deriva del set de
 * variantes para no reportar "off" solo porque cada variante tiene su propio
 * inventory item (no existe un único item padre en productos variables).
 */
export function resolveVariantInventoryState(
  productStructure: string | null | undefined,
  variantInventoryState: VariantInventoryState | null | undefined,
  variants: ReadonlyArray<InventoryManagedRow> | null | undefined
): VariantInventoryState | null {
  if (productStructure !== 'variable') {
    return null;
  }
  if (variantInventoryState) {
    return variantInventoryState;
  }

  const rows = variants ?? [];
  if (!rows.length) {
    return 'off';
  }

  const managedCount = rows.filter((variant) => !!variant.inventoryManaged).length;
  if (managedCount === 0) {
    return 'off';
  }
  return managedCount === rows.length ? 'active' : 'incomplete';
}

function toStock(value: number | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Vínculo residual con existencias (p. ej. artículo inactivo): el dueño aún
 * debe poder “apagar” el control y ver el modal de ajuste a cero.
 */
export function hasResidualInventoryStockClaim(
  productStructure: string | null | undefined,
  variants: ReadonlyArray<InventoryClaimRow> | null | undefined,
  residual?: {
    inventoryItemId?: string | null;
    stockQuantity?: number | null;
  }
): boolean {
  if (productStructure === 'variable') {
    return (variants ?? []).some(
      (variant) => !!variant.inventoryItemId && toStock(variant.stockQuantity) > 0
    );
  }

  return !!residual?.inventoryItemId && toStock(residual.stockQuantity) > 0;
}

/**
 * Boolean para el toggle "Controlar inventario" y UI asociada.
 *
 * - Operable (`inventoryManaged` / variantes active|incomplete), o
 * - Residual con stock > 0 (vínculo a item inactivo): el switch debe verse ON
 *   para que al apagarlo salga el modal de ajuste a cero.
 */
export function resolveEffectiveInventoryManaged(
  productStructure: string | null | undefined,
  inventoryManaged: boolean | null | undefined,
  variantInventoryState: VariantInventoryState | null | undefined,
  variants: ReadonlyArray<InventoryClaimRow> | null | undefined,
  residual?: {
    inventoryItemId?: string | null;
    stockQuantity?: number | null;
  }
): boolean {
  if (productStructure === 'variable') {
    const state = resolveVariantInventoryState(
      productStructure,
      variantInventoryState,
      asManagedRows(variants)
    );
    if (state !== null && state !== 'off') {
      return true;
    }
    return hasResidualInventoryStockClaim(productStructure, variants);
  }

  if (inventoryManaged) {
    return true;
  }

  return hasResidualInventoryStockClaim(productStructure, variants, residual);
}
